import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { OwnerSelect } from '@/components/merchant/OwnerSelect';
import { MobileOtpVerify } from '@/components/merchant/MobileOtpVerify';

interface FormData {
  companyName: string;
  brandName?: string;
  email: string;
  mobile: string;
  password: string;
  serviceType: 'B2C' | 'B2B' | 'BOTH';
  billingMode: 'PREPAID' | 'POSTPAID';
  postpaidLimit?: number;
  remittanceTatDays: number;
}

export function CreateMerchantForm({ onSuccess }: { onSuccess: (created?: any) => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: { serviceType: 'BOTH', billingMode: 'PREPAID', remittanceTatDays: 5, postpaidLimit: 0 },
  });
  // Account ownership — reported on later, so it's captured at creation time.
  const [salesUserId, setSalesUserId] = useState<number | null>(null);
  const [csUserId, setCsUserId] = useState<number | null>(null);
  // The verified OTP for the mobile below — the server refuses to create the merchant without it.
  const [otpId, setOtpId] = useState<string | null>(null);

  const billingMode = watch('billingMode');
  const mobile = watch('mobile') ?? '';

  const create = useMutation({
    mutationFn: async (data: FormData) => (await api.post('/merchants', {
      ...data,
      otpId,
      ...(salesUserId ? { salesUserId } : {}),
      ...(csUserId ? { csUserId } : {}),
    })).data,
    onSuccess: (created: any) => {
      qc.invalidateQueries({ queryKey: ['merchants'] });
      toast.success('Merchant created');
      onSuccess(created);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create merchant'),
  });

  return (
    <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Company Name *" error={errors.companyName?.message}>
          <input {...register('companyName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })}
            placeholder="Acme Logistics Pvt Ltd" className="input" />
        </Field>
        <Field label="Brand Name">
          <input {...register('brandName')} placeholder="Acme" className="input" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Email *" error={errors.email?.message}>
          <input type="email" {...register('email', { required: 'Required' })}
            placeholder="contact@acme.com" className="input" />
        </Field>
        <Field label="Mobile *" error={errors.mobile?.message}>
          <input {...register('mobile', { required: 'Required', pattern: { value: /^[6-9]\d{9}$/, message: '10 digits starting 6-9' } })}
            placeholder="9876543210" className="input" />
          <MobileOtpVerify mobile={mobile} purpose="MERCHANT_ADD" otpId={otpId} onVerified={setOtpId} disabled={create.isPending} />
        </Field>
      </div>

      <Field label="Password * (merchant uses this to log in)" error={errors.password?.message}>
        <input type="text" {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })}
          placeholder="At least 8 characters" className="input font-mono text-sm" />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Service Type">
          <select {...register('serviceType')} className="input">
            <option value="BOTH">Both B2C & B2B</option>
            <option value="B2C">B2C Only</option>
            <option value="B2B">B2B Only</option>
          </select>
        </Field>
        <Field label="Billing Mode">
          <select {...register('billingMode')} className="input">
            <option value="PREPAID">Prepaid</option>
            <option value="POSTPAID">Postpaid</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {billingMode === 'POSTPAID' && (
          <Field label="Postpaid Limit (₹)">
            <input type="number" step="0.01" {...register('postpaidLimit', { valueAsNumber: true, min: 0 })}
              placeholder="50000" className="input" />
          </Field>
        )}
        <Field label="Remittance TAT (days)">
          <input type="number" {...register('remittanceTatDays', { valueAsNumber: true, min: 1 })}
            placeholder="5" className="input" />
        </Field>
      </div>

      {/* Account ownership — drives sales / support reporting */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Sales Person">
          <OwnerSelect value={salesUserId} onChange={setSalesUserId} preferType="SALES_PERSON" placeholder="Select sales person" />
        </Field>
        <Field label="CS Person (Customer Support)">
          <OwnerSelect value={csUserId} onChange={setCsUserId} preferType="CUSTOMER_SUPPORT" placeholder="Select CS person" />
        </Field>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        💡 Admin-created merchants are auto-approved. They can log in immediately at the Merchant portal using the email/password set above.
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={() => onSuccess()} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={create.isPending || !otpId}
          title={!otpId ? 'Verify the mobile number by OTP first' : undefined}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {create.isPending && <Loader2 size={16} className="animate-spin" />}
          Create Merchant
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
